import {useEffect, useMemo} from 'react';

import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';

export const NEXT_URL_KEY = 'next';
export const AUTH_TRACKING_PARAMS_KEY = 'auth_tracking_params';
export const AUTH_TRACKING_PARAM_PREFIXES = ['utm_', '_gl'];

export const isAuthTrackingParam = (paramName = '') => {
  return AUTH_TRACKING_PARAM_PREFIXES.some((prefix) => paramName.startsWith(prefix));
};

export const extractAuthTrackingParams = (search = '') => {
  const searchParams = new URLSearchParams(search);
  const trackingParams = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (isAuthTrackingParam(key)) {
      trackingParams.set(key, value);
    }
  }

  return trackingParams.toString();
};

const DUMMY_BASE = 'http://__dummy.local__';

export const appendAuthTrackingParams = (url, trackingParamsString) => {
  if (!url || !trackingParamsString) {
    return url;
  }

  const parsedUrl = new URL(url, DUMMY_BASE);
  const isAbsolute = parsedUrl.origin !== DUMMY_BASE || url.startsWith(DUMMY_BASE);
  const trackingParams = new URLSearchParams(trackingParamsString);

  const keysToDelete = new Set(trackingParams.keys());
  keysToDelete.forEach((key) => parsedUrl.searchParams.delete(key));
  trackingParams.forEach((value, key) => parsedUrl.searchParams.append(key, value));

  if (isAbsolute) {
    return parsedUrl.toString();
  }

  return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
};

export const useNextUrl = () => {
  const redirect = (secondaryUrl) => {
    const nextUrl = sessionStorage.getItem(NEXT_URL_KEY);
    const authTrackingParams = sessionStorage.getItem(AUTH_TRACKING_PARAMS_KEY);
    sessionStorage.removeItem(NEXT_URL_KEY);
    sessionStorage.removeItem(AUTH_TRACKING_PARAMS_KEY);
    window.location.href = appendAuthTrackingParams(
      nextUrl || secondaryUrl || '/',
      authTrackingParams,
    );
  };

  return {redirect};
};

export default function NextUrlProvider({children}) {
  const location = useLocation();
  const {url, authTrackingParams} = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      url: search.get(NEXT_URL_KEY),
      authTrackingParams: extractAuthTrackingParams(location.search),
    };
  }, [location.search]);

  useEffect(() => {
    if (url) {
      sessionStorage.setItem(NEXT_URL_KEY, url);
    }

    if (authTrackingParams) {
      sessionStorage.setItem(AUTH_TRACKING_PARAMS_KEY, authTrackingParams);
    }
  }, [authTrackingParams, url]);

  return children;
}

NextUrlProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
